import * as React from 'react'

/**
 * Reports whether `text` fits on one line inside the container. `measureRef`
 * must be attached to a hidden, non-wrapping span holding the full text, and
 * `containerRef` to the box that bounds the visible text. Measuring a hidden
 * node (not the visible one) keeps a switch to name-only from flipping back.
 */
export const useFitsWidth = (text: string) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const measureRef = React.useRef<HTMLSpanElement | null>(null)
  const [fits, setFits] = React.useState(true)

  const measure = React.useCallback(() => {
    const container = containerRef.current
    const measurer = measureRef.current
    if (!container || !measurer) return

    const next = measurer.scrollWidth <= container.clientWidth
    setFits((current) => (current === next ? current : next))
  }, [])

  React.useLayoutEffect(() => {
    measure()

    const container = containerRef.current
    if (!container || typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(measure)
    observer.observe(container)

    return () => observer.disconnect()
  }, [measure, text])

  return { containerRef, measureRef, fits }
}
